import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
export const LoginModal = ({ isOpen, onClose, initialEmail, message, onLoginSuccess }) => {
    const { login } = useAuth();
    const [modalMode, setModalMode] = useState('login');
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [forgotPasswordErrors, setForgotPasswordErrors] = useState({});
    const [resendInfo, setResendInfo] = useState('');
    const [isResendingVerification, setIsResendingVerification] = useState(false);
    // Effect to set initial email when modal opens
    React.useEffect(() => {
        if (isOpen && initialEmail) {
            setFormData(prev => ({ ...prev, email: initialEmail }));
            setForgotPasswordEmail(initialEmail);
        }
    }, [isOpen, initialEmail]);
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear field-specific errors when user types
        if (field === 'email' && errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }));
        }
        if (field === 'password' && errors.password) {
            setErrors(prev => ({ ...prev, password: undefined }));
        }
        // Clear general error when user makes changes
        if (errors.general) {
            setErrors(prev => ({ ...prev, general: undefined }));
        }
        if (resendInfo) {
            setResendInfo('');
        }
    };
    const handleResendVerification = async () => {
        const email = formData.email.trim().toLowerCase();
        if (!email) {
            setResendInfo('Enter your email above first, then try again.');
            return;
        }
        setIsResendingVerification(true);
        setResendInfo('');
        try {
            const result = await authService.resendVerificationEmail(email);
            setResendInfo(result.message || 'If your account still needs verifying, a new link is on its way. Check your inbox and spam.');
        }
        catch (err) {
            console.error('Failed to resend verification email:', err);
            setResendInfo('Could not resend right now. Please try again.');
        }
        finally {
            setIsResendingVerification(false);
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        setErrors({});
        try {
            await login({
                email: formData.email.trim().toLowerCase(),
                password: formData.password
            });
            // Login successful
            if (onLoginSuccess) {
                // Custom success handler (e.g., for claim bundle page)
                setFormData({ email: '', password: '' });
                setErrors({});
                setModalMode('login');
                onLoginSuccess();
            }
            else {
                // Default: close modal and reset form
                handleClose();
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Login failed';
            setErrors({ general: errorMessage });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleGoogleLogin = async () => {
        setIsGoogleLoading(true);
        setErrors(prev => ({ ...prev, general: undefined }));
        try {
            // Start external login and redirect to provider
            const url = await authService.startExternalLogin('GOOGLE');
            window.location.href = url;
        }
        catch (err) {
            console.error('Failed to start Google login', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to start Google login';
            setErrors(prev => ({ ...prev, general: errorMessage }));
            setIsGoogleLoading(false);
        }
    };
    const validateForgotPasswordForm = () => {
        const newErrors = {};
        if (!forgotPasswordEmail.trim()) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(forgotPasswordEmail)) {
            newErrors.email = 'Please enter a valid email address';
        }
        setForgotPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validateForgotPasswordForm()) {
            return;
        }
        setIsSubmitting(true);
        setForgotPasswordErrors({});
        try {
            const result = await authService.requestPasswordResetByEmail(forgotPasswordEmail);
            if (result.success) {
                setForgotPasswordErrors({ general: result.message || 'Password reset email sent! Please check your inbox.' });
            }
            else {
                setForgotPasswordErrors({ general: result.message || 'Failed to send password reset email. Please try again.' });
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset email';
            setForgotPasswordErrors({ general: errorMessage });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleForgotPasswordEmailChange = (value) => {
        setForgotPasswordEmail(value);
        // Clear errors when user types
        if (forgotPasswordErrors.email) {
            setForgotPasswordErrors(prev => ({ ...prev, email: undefined }));
        }
        if (forgotPasswordErrors.general) {
            setForgotPasswordErrors(prev => ({ ...prev, general: undefined }));
        }
    };
    const switchToForgotPassword = () => {
        setModalMode('forgot-password');
        setErrors({});
        setForgotPasswordErrors({});
    };
    const switchToLogin = () => {
        setModalMode('login');
        setErrors({});
        setForgotPasswordErrors({});
        setForgotPasswordEmail('');
    };
    const handleClose = () => {
        // Reset form when closing
        setFormData({ email: '', password: '' });
        setForgotPasswordEmail('');
        setErrors({});
        setForgotPasswordErrors({});
        setModalMode('login');
        setIsGoogleLoading(false);
        onClose();
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[70] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: handleClose }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-md flex flex-col", style: { maxHeight: '92vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--sand)' }, children: modalMode === 'login' ? 'Log In' : 'Reset Password' }), _jsx("button", { onClick: handleClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { size: 24 }) })] }), _jsxs("div", { className: "p-6 overflow-y-auto min-h-0 flex-1 ls-modal-band-bottom", style: { backgroundColor: 'var(--sand)' }, children: [modalMode === 'login' ? (_jsxs(_Fragment, { children: [message && (_jsx("div", { className: "mb-4 p-3 rounded-lg border", style: { backgroundColor: 'var(--dew)', borderColor: 'var(--pewter)' }, children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: message }) })), errors.general && (_jsxs("div", { className: "mb-4 p-3 rounded-lg bg-red-50 border border-red-200", children: [_jsx("p", { className: "text-red-600 text-sm", children: errors.general }), _jsxs("p", { className: "text-sm mt-2", style: { color: 'var(--charcoal)' }, children: ["Haven't verified your email yet?", ' ', _jsx("button", { type: "button", onClick: handleResendVerification, disabled: isResendingVerification, className: "font-medium underline hover:opacity-80 disabled:opacity-50", style: { color: 'var(--forest)' }, children: isResendingVerification ? 'Sending…' : 'Resend verification link' })] })] })), resendInfo && (_jsx("div", { className: "mb-4 p-3 rounded-lg bg-green-50 border border-green-200", children: _jsx("p", { className: "text-green-700 text-sm", children: resendInfo }) })), _jsx("button", { onClick: handleGoogleLogin, disabled: isGoogleLoading || isSubmitting, className: "w-full flex items-center justify-center gap-3 p-3 mb-6 border rounded-lg hover:bg-opacity-10 hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed", style: {
                                            borderColor: 'var(--pewter)',
                                            backgroundColor: 'white',
                                            color: 'var(--charcoal)'
                                        }, children: isGoogleLoading ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" }), _jsx("span", { className: "font-medium", children: "Connecting to Google..." })] })) : (_jsxs(_Fragment, { children: [_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", children: [_jsx("path", { fill: "#4285F4", d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" }), _jsx("path", { fill: "#34A853", d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" }), _jsx("path", { fill: "#FBBC05", d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" }), _jsx("path", { fill: "#EA4335", d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" })] }), _jsx("span", { className: "font-medium", children: "Log in with Google" })] })) }), _jsxs("div", { className: "relative mb-6", children: [_jsx("div", { className: "absolute inset-0 flex items-center", children: _jsx("div", { className: "w-full border-t", style: { borderColor: 'var(--pewter)' } }) }), _jsx("div", { className: "relative flex justify-center text-sm", children: _jsx("span", { className: "px-2 bg-white", style: {
                                                        backgroundColor: 'var(--sand)',
                                                        color: 'var(--charcoal)'
                                                    }, children: "or" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "email", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "Email" }), _jsx("input", { type: "email", id: "email", autoComplete: "username", value: formData.email, onChange: (e) => handleInputChange('email', e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.email ? 'border-red-500' : ''}`, style: {
                                                            borderColor: errors.email ? '#ef4444' : 'var(--pewter)',
                                                            backgroundColor: 'white',
                                                            color: 'var(--charcoal)'
                                                        }, placeholder: "Enter your email", disabled: isSubmitting }), errors.email && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors.email }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "password", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "Password" }), _jsx("input", { type: "password", id: "password", autoComplete: "current-password", value: formData.password, onChange: (e) => handleInputChange('password', e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.password ? 'border-red-500' : ''}`, style: {
                                                            borderColor: errors.password ? '#ef4444' : 'var(--pewter)',
                                                            backgroundColor: 'white',
                                                            color: 'var(--charcoal)'
                                                        }, placeholder: "Enter your password", disabled: isSubmitting }), errors.password && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors.password }))] }), _jsx("div", { className: "text-right", children: _jsx("button", { type: "button", onClick: switchToForgotPassword, className: "text-sm underline hover:opacity-80 transition-opacity", style: { color: 'var(--pewter)' }, children: "Forgot password?" }) }), _jsx("button", { type: "submit", disabled: isSubmitting, className: "w-full py-3 px-4 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50", style: {
                                                    backgroundColor: 'var(--forest)',
                                                    color: 'var(--sand)'
                                                }, children: isSubmitting ? 'Logging in...' : 'Log In' })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "mb-4", children: _jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Please enter your email address to request a password reset." }) }), forgotPasswordErrors.general && (_jsx("div", { className: `mb-4 p-3 rounded-lg border ${forgotPasswordErrors.general.includes('sent') || forgotPasswordErrors.general.includes('instructions have been sent')
                                            ? 'bg-green-50 border-green-200'
                                            : 'bg-red-50 border-red-200'}`, children: _jsx("p", { className: `text-sm ${forgotPasswordErrors.general.includes('sent') || forgotPasswordErrors.general.includes('instructions have been sent')
                                                ? 'text-green-600'
                                                : 'text-red-600'}`, children: forgotPasswordErrors.general }) })), _jsxs("form", { onSubmit: handleForgotPasswordSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "forgot-email", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "Email" }), _jsx("input", { type: "email", id: "forgot-email", value: forgotPasswordEmail, onChange: (e) => handleForgotPasswordEmailChange(e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${forgotPasswordErrors.email ? 'border-red-500' : ''}`, style: {
                                                            borderColor: forgotPasswordErrors.email ? '#ef4444' : 'var(--pewter)',
                                                            backgroundColor: 'white',
                                                            color: 'var(--charcoal)'
                                                        }, placeholder: "Enter your email address", disabled: isSubmitting, required: true }), forgotPasswordErrors.email && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: forgotPasswordErrors.email }))] }), _jsx("button", { type: "submit", disabled: isSubmitting, className: "w-full py-3 px-4 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50", style: {
                                                    backgroundColor: 'var(--forest)',
                                                    color: 'var(--sand)'
                                                }, children: isSubmitting ? 'Sending...' : 'Send Reset Email' })] }), _jsx("div", { className: "text-center mt-4", children: _jsx("button", { type: "button", onClick: switchToLogin, className: "text-sm underline hover:opacity-80 transition-opacity", style: { color: 'var(--pewter)' }, children: "Back to login" }) })] })), _jsx("div", { className: "text-center mt-4", children: _jsxs("span", { className: "text-xs", style: { color: 'var(--pewter)' }, children: [_jsx("a", { href: `${REACT_APP_BASE_PATH}/privacy-policy`, target: "_blank", rel: "noopener noreferrer", className: "underline hover:opacity-80 transition-opacity", style: { color: 'var(--pewter)' }, children: "Privacy Policy" }), ' | ', _jsx("a", { href: `${REACT_APP_BASE_PATH}/terms-of-service`, target: "_blank", rel: "noopener noreferrer", className: "underline hover:opacity-80 transition-opacity", style: { color: 'var(--pewter)' }, children: "Terms of Use" })] }) })] })] })] }));
};
